import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface PlanStrength {
  pillar_name: string;
}

export interface PlanGap {
  section_name: string;
  pillar_name: string;
}

export interface PlanData {
  generated_at: string;
  overall_score: number | null;
  pillars: { pillar_name: string; score: number }[];
  strengths: PlanStrength[];
  gaps: PlanGap[];
  succession_strategy: {
    pathway: string | null;
    target_exit_date: string | null;
  };
  business_profile: {
    valuation_amount: number | null;
    valuation_low: number | null;
    valuation_high: number | null;
    valuation_available: boolean;
  };
  advisory_team: {
    members_in_place: number;
    member_names: string[];
  };
  successors: {
    count: number;
    names: string[];
  };
  tax_structure: {
    incorporated: boolean;
    lcge_eligible: boolean | null;
  };
  governance: {
    family_involved: boolean | null;
  };
}

interface UsePlanSynthesisReturn {
  planData: PlanData | null;
  isLoading: boolean;
  error: Error | null;
}

async function fetchOverallScore(userId: string): Promise<number | null> {
  try {
    const { data, error } = await supabase
      .from('user_scores')
      .select('score_value')
      .eq('user_id', userId)
      .eq('entity_type', 'overall')
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data?.score_value != null ? Number(data.score_value) : null;
  } catch (e) {
    logger.error('usePlanSynthesis: fetchOverallScore failed', e);
    return null;
  }
}

async function fetchPillarScores(userId: string): Promise<{ pillar_name: string; score: number }[]> {
  try {
    const { data, error } = await supabase
      .from('user_scores')
      .select('score_value, entity_id')
      .eq('user_id', userId)
      .eq('entity_type', 'pillar');
    if (error) throw error;
    if (!data || data.length === 0) return [];

    const pillarIds = data.map((d) => d.entity_id).filter(Boolean) as string[];
    const { data: pillars, error: pErr } = await supabase
      .from('scoring_pillars')
      .select('id, title')
      .in('id', pillarIds);
    if (pErr) throw pErr;

    const pillarMap = new Map((pillars ?? []).map((p) => [p.id, p.title]));
    return data
      .map((d) => ({
        pillar_name: pillarMap.get(d.entity_id as string) ?? 'Unknown',
        score: Number(d.score_value),
      }))
      .sort((a, b) => b.score - a.score);
  } catch (e) {
    logger.error('usePlanSynthesis: fetchPillarScores failed', e);
    return [];
  }
}

async function fetchGaps(userId: string): Promise<PlanGap[]> {
  try {
    const { data, error } = await supabase
      .from('user_scores')
      .select('score_value, entity_id')
      .eq('user_id', userId)
      .eq('entity_type', 'section')
      .order('score_value', { ascending: true })
      .limit(3);
    if (error) throw error;
    if (!data || data.length === 0) return [];

    const sectionIds = data.map((d) => d.entity_id).filter(Boolean) as string[];
    const { data: sections, error: sErr } = await supabase
      .from('scoring_sections')
      .select('id, section_name, pillar_id')
      .in('id', sectionIds);
    if (sErr) throw sErr;

    const pillarIds = Array.from(new Set((sections ?? []).map((s) => s.pillar_id)));
    const { data: pillars, error: pErr } = await supabase
      .from('scoring_pillars')
      .select('id, title')
      .in('id', pillarIds);
    if (pErr) throw pErr;

    const sectionMap = new Map(
      (sections ?? []).map((s) => [s.id, { section_name: s.section_name, pillar_id: s.pillar_id }])
    );
    const pillarMap = new Map((pillars ?? []).map((p) => [p.id, p.title]));

    return data.map((d) => {
      const sec = sectionMap.get(d.entity_id as string);
      return {
        section_name: sec?.section_name ?? 'Unknown',
        pillar_name: sec ? (pillarMap.get(sec.pillar_id) ?? 'Unknown') : 'Unknown',
      };
    });
  } catch (e) {
    logger.error('usePlanSynthesis: fetchGaps failed', e);
    return [];
  }
}

async function fetchSuccessionPathway(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('assessment_progress')
      .select('answers')
      .eq('user_id', userId)
      .eq('module_id', 'successor-strategy')
      .maybeSingle();
    if (error) throw error;
    const answers = data?.answers as Record<string, unknown> | null;
    const labelMap: Record<string, string> = {
      'internal_transition': 'Internal Transition',
      'management_buyout': 'Management Buyout',
      'family_succession': 'Family Succession',
      'external_sale': 'External Sale',
      'merger_acquisition': 'Merger or Acquisition',
      'employee_ownership': 'Employee Ownership (EOT)',
      'wind_down': 'Wind Down',
    };
    const raw = (answers?.exit_route_primary as string) ?? null;
    return raw ? (labelMap[raw] ?? raw) : null;
  } catch (e) {
    logger.error('usePlanSynthesis: fetchSuccessionPathway failed', e);
    return null;
  }
}

async function fetchTargetExitDate(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('assessment_progress')
      .select('answers')
      .eq('user_id', userId)
      .eq('module_id', 'owner-readiness-exit-timeline')
      .maybeSingle();
    if (error) throw error;
    const answers = data?.answers as Record<string, unknown> | null;
    return (answers?.target_exit_date as string) ?? null;
  } catch (e) {
    logger.error('usePlanSynthesis: fetchTargetExitDate failed', e);
    return null;
  }
}

async function fetchValuation(userId: string): Promise<PlanData['business_profile']> {
  const empty: PlanData['business_profile'] = {
    valuation_amount: null,
    valuation_low: null,
    valuation_high: null,
    valuation_available: false,
  };
  try {
    const { data, error } = await supabase
      .from('valuation_results')
      .select('valuation_amount')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    if (!data || data.valuation_amount == null) return empty;
    const amt = Number(data.valuation_amount);
    return {
      valuation_amount: amt,
      valuation_low: amt * 0.9,
      valuation_high: amt * 1.1,
      valuation_available: true,
    };
  } catch (e) {
    logger.error('usePlanSynthesis: fetchValuation failed', e);
    return empty;
  }
}

async function fetchAdvisoryTeam(userId: string): Promise<PlanData['advisory_team']> {
  const empty: PlanData['advisory_team'] = { members_in_place: 0, member_names: [] };
  try {
    // Find the user's team
    const { data: membership, error: mErr } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('user_id', userId)
      .eq('status', 'active')
      .eq('member_role', 'owner')
      .limit(1)
      .maybeSingle();
    if (mErr) throw mErr;
    if (!membership) return empty;

    const { data: members, error: tErr } = await supabase
      .from('team_members')
      .select('full_name')
      .eq('team_id', membership.team_id)
      .eq('status', 'active')
      .neq('member_role', 'owner');
    if (tErr) throw tErr;
    if (!members) return empty;

    return {
      members_in_place: members.length,
      member_names: members.map((m) => m.full_name).filter(Boolean) as string[],
    };
  } catch (e) {
    logger.error('usePlanSynthesis: fetchAdvisoryTeam failed', e);
    return empty;
  }
}

async function fetchSuccessors(userId: string): Promise<PlanData['successors']> {
  const empty: PlanData['successors'] = { count: 0, names: [] };
  try {
    const { data, error } = await supabase
      .from('successor_candidates')
      .select('full_name')
      .eq('user_id', userId)
      .eq('status', 'selected');
    if (error) throw error;
    if (!data) return empty;
    return {
      count: data.length,
      names: data.map((d) => d.full_name).filter(Boolean),
    };
  } catch (e) {
    logger.error('usePlanSynthesis: fetchSuccessors failed', e);
    return empty;
  }
}

async function fetchTaxStructure(userId: string): Promise<PlanData['tax_structure']> {
  const fallback: PlanData['tax_structure'] = { incorporated: false, lcge_eligible: null };
  try {
    const { data: profile, error: pErr } = await supabase
      .from('profiles')
      .select('business_structure')
      .eq('id', userId)
      .maybeSingle();
    if (pErr) throw pErr;

    const incorporated =
      (profile?.business_structure ?? '').toLowerCase() === 'corporation';

    if (!incorporated) return { incorporated: false, lcge_eligible: null };

    // Only read LCGE if incorporated
    const { data: progress, error: aErr } = await supabase
      .from('assessment_progress')
      .select('answers')
      .eq('user_id', userId)
      .eq('module_id', 'exit-structure')
      .maybeSingle();
    if (aErr) throw aErr;

    const answers = progress?.answers as Record<string, unknown> | null;
    let lcge: boolean | null = null;
    const val = answers?.lcge_eligibility as string | undefined;
    if (val != null) {
      lcge = val === 'likely_eligible' || val === 'eligible' || val === 'yes_eligible';
    }

    return { incorporated: true, lcge_eligible: lcge };
  } catch (e) {
    logger.error('usePlanSynthesis: fetchTaxStructure failed', e);
    return fallback;
  }
}

async function fetchGovernance(userId: string): Promise<PlanData['governance']> {
  try {
    const { data, error } = await supabase
      .from('assessment_progress')
      .select('answers')
      .eq('user_id', userId)
      .eq('module_id', 'governance-family')
      .maybeSingle();
    if (error) throw error;
    if (!data) return { family_involved: null };

    const answers = data.answers as Record<string, unknown> | null;
    const involved = answers?.family_members_involved as string[] | undefined;
    if (!involved || involved.length === 0) {
      return { family_involved: false };
    }
    const hasNone = involved.length === 1 && involved[0] === 'none';
    return { family_involved: !hasNone };
  } catch (e) {
    logger.error('usePlanSynthesis: fetchGovernance failed', e);
    return { family_involved: null };
  }
}

export const usePlanSynthesis = (): UsePlanSynthesisReturn => {
  const [planData, setPlanData] = useState<PlanData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const synthesize = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user?.id ?? '';

        if (!userId) {
          setIsLoading(false);
          return;
        }

        const [
          overallScore,
          pillarScores,
          gaps,
          pathway,
          exitDate,
          valuation,
          advisoryTeam,
          successors,
          taxStructure,
          governance,
        ] = await Promise.all([
          fetchOverallScore(userId),
          fetchPillarScores(userId),
          fetchGaps(userId),
          fetchSuccessionPathway(userId),
          fetchTargetExitDate(userId),
          fetchValuation(userId),
          fetchAdvisoryTeam(userId),
          fetchSuccessors(userId),
          fetchTaxStructure(userId),
          fetchGovernance(userId),
        ]);

        if (cancelled) return;

        const strengths: PlanStrength[] = pillarScores
          .slice(0, 2)
          .map((p) => ({ pillar_name: p.pillar_name }));

        const data: PlanData = {
          generated_at: new Date().toISOString(),
          overall_score: overallScore,
          pillars: pillarScores,
          strengths,
          gaps,
          succession_strategy: {
            pathway,
            target_exit_date: exitDate,
          },
          business_profile: valuation,
          advisory_team: advisoryTeam,
          successors,
          tax_structure: taxStructure,
          governance,
        };

        setPlanData(data);
      } catch (e) {
        if (!cancelled) {
          const err = e instanceof Error ? e : new Error(String(e));
          logger.error('usePlanSynthesis: synthesis failed', err);
          setError(err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    synthesize();
    return () => { cancelled = true; };
  }, []);

  return { planData, isLoading, error };
};
